/**
 * Polling that pauses while the tab is in the background (medopd-docs 107).
 *
 * TanStack Query's refetchInterval already pauses in a hidden tab. The hand-written setInterval loops did not: a
 * forgotten tab kept calling the API every few seconds for as long as it stayed open — server load for nothing. This
 * runs `fn` every `ms` while the page is visible, skips ticks while it is hidden, and when the tab comes back runs
 * `fn` at once if a tick was missed, so the screen is fresh the moment someone looks at it.
 *
 * @returns a cleanup function (use it as the effect's return value).
 */
export function everyWhileVisible(fn: () => void, ms: number): () => void {
  const hidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
  let missed = false;
  const id = setInterval(() => {
    if (hidden()) { missed = true; return; }
    fn();
  }, ms);
  const onVisible = () => {
    if (!hidden() && missed) {
      missed = false;
      fn();
    }
  };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisible);
  return () => {
    clearInterval(id);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisible);
  };
}
